const ARROW_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

export function isArrowNavKey(key) {
  return ARROW_KEYS.has(key);
}

/**
 * Next index when Tab / Shift+Tab stays inside one widget and wraps.
 */
export function nextWrappedLinearIndex(index, length, backward = false) {
  if (length <= 0 || index < 0) return -1;
  if (backward) return (index - 1 + length) % length;
  return (index + 1) % length;
}

/**
 * Next index in a linear list (stringing order / recipe order).
 * Down and Right advance; Up and Left go back.
 */
export function nextLinearIndex(key, index, length) {
  if (length <= 0 || index < 0) return -1;
  if (key === "Home") return 0;
  if (key === "End") return length - 1;
  if (key === "ArrowRight" || key === "ArrowDown") {
    return Math.min(length - 1, index + 1);
  }
  if (key === "ArrowLeft" || key === "ArrowUp") {
    return Math.max(0, index - 1);
  }
  return -1;
}

/**
 * Next bead index on the stringing chart. Rows read right to left, so Left
 * advances along the string and Right goes back within the row. Up/Down keep
 * the same visual column.
 * `rows` is an array of bead-index arrays in stringing order within each row.
 */
export function nextGridBeadIndex(key, beadIndex, rows) {
  const grid = Array.isArray(rows) ? rows : [];
  if (grid.length === 0) return -1;

  let row = -1;
  let col = -1;
  for (let r = 0; r < grid.length; r++) {
    const c = grid[r].indexOf(beadIndex);
    if (c >= 0) {
      row = r;
      col = c;
      break;
    }
  }
  if (row < 0) return -1;

  const flat = grid.flat();
  if (key === "Home") return flat[0] ?? -1;
  if (key === "End") return flat[flat.length - 1] ?? -1;

  const visualCol = grid[row].length - 1 - col;

  if (key === "ArrowLeft") {
    // Next in reading order (toward the left of the row).
    if (col < grid[row].length - 1) return grid[row][col + 1];
    if (row < grid.length - 1) return grid[row + 1][0];
    return beadIndex;
  }

  if (key === "ArrowRight") {
    // Back toward the right / start of the row.
    if (col > 0) return grid[row][col - 1];
    if (row > 0) return grid[row - 1][grid[row - 1].length - 1];
    return beadIndex;
  }

  if (key === "ArrowDown") {
    if (row >= grid.length - 1) return beadIndex;
    const next = grid[row + 1];
    const targetVisual = Math.min(visualCol, next.length - 1);
    return next[next.length - 1 - targetVisual];
  }

  if (key === "ArrowUp") {
    if (row <= 0) return beadIndex;
    const prev = grid[row - 1];
    const targetVisual = Math.min(visualCol, prev.length - 1);
    return prev[prev.length - 1 - targetVisual];
  }

  return -1;
}

/** Focus a sibling/list item after an arrow key, if navigation applies. */
export function moveFocusByArrowKey(event, {
  items,
  currentIndex,
  nextIndex,
}) {
  if (!isArrowNavKey(event.key)) return false;
  const list = Array.isArray(items) ? items : [];
  const from =
    Number.isInteger(currentIndex) && currentIndex >= 0
      ? currentIndex
      : list.indexOf(event.currentTarget);
  if (from < 0 || list.length === 0) return false;

  const targetIndex =
    typeof nextIndex === "function"
      ? nextIndex(event.key, from, list)
      : nextLinearIndex(event.key, from, list.length);
  if (targetIndex < 0 || targetIndex === from) {
    if (isArrowNavKey(event.key)) event.preventDefault();
    return targetIndex === from;
  }

  const target = list[targetIndex];
  if (!target) return false;
  event.preventDefault();
  target.focus();
  return true;
}
